import { Form, Head, Link, router, usePage } from "@inertiajs/react"
import {
  CheckCircle2,
  Clock,
  LogOut,
  MailX,
  ShieldAlert,
  XCircle,
} from "lucide-react"
import { useEffect, useRef } from "react"
import { Trans, useTranslation } from "react-i18next"

import { BrandCoverBand } from "@/components/brand/brand-cover-band"
import { BrandLogo } from "@/components/brand-logo"
import { EmptyState } from "@/components/empty-state"
import InputError from "@/components/input-error"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import { useOnline } from "@/hooks/use-online"
import { type InvitationAccountType, roleLabel } from "@/lib/role-label"
import { cn } from "@/lib/utils"
import { acceptInvitationPath, rootPath, sessionPath } from "@/routes"
import type { CurrentSession, SharedProps } from "@/types"
import type {
  InvitationPayload,
  InvitationsAcceptProps,
} from "@/types/invitations"

/**
 * OKS-11 — Invitations/Accept.
 *
 * One Inertia page, six state branches (CTPO D2 + D3 + D4):
 *
 *   - valid    → password + name form, Inertia <Form> submit
 *   - expired  → friendly EmptyState, single "Log in" CTA
 *   - accepted → friendly EmptyState, "You've already accepted" + log-in CTA
 *   - revoked  → friendly EmptyState, contact-inviter copy (no revoker attribution)
 *   - mismatch → warning EmptyState, "Log out to continue / Cancel" — explicit
 *                sign-out only, never automatic (Rule 12 / D3)
 *   - rejected → terminal error EmptyState + mailto:{support_email} CTA (Rule 8a / D4)
 *
 * Public route — no shell, no nav. Page chooses its own layout so the
 * valid state gets a branded hero, and the state pages share a compact
 * EmptyState variant. All copy flows through i18next; email bound to the
 * invitation is rendered read-only (never editable — Rule 5).
 */
export default function InvitationsAccept({
  state,
  token,
  invitation,
  sign_in_path,
  support_email,
}: InvitationsAcceptProps) {
  const { t } = useTranslation()

  // Focus the main landmark on mount so screen readers + keyboard users land
  // on the state announcement.
  const mainRef = useRef<HTMLElement>(null)
  useEffect(() => {
    mainRef.current?.focus()
  }, [])

  // OKS-304 — Brand cover hero on the valid state ONLY (UX Spec §2.2).
  // Non-valid invite states (expired / accepted / revoked / mismatch /
  // rejected) are utility surfaces; brand identity is data on those
  // (logo + name in the EmptyState), not aspirational chrome.
  const showCoverBand =
    state === "valid" && invitation?.target_account?.type === "brand"

  return (
    <>
      <Head title={t(`invitations.accept.${state}.head_title`)} />
      <div
        className="bg-background flex min-h-svh flex-col"
        data-testid={`invite-state-${state}`}
      >
        {showCoverBand ? (
          <BrandCoverBand
            coverUrl={invitation?.target_account?.cover_image_url ?? null}
            brandName={invitation?.target_account?.name ?? ""}
          />
        ) : null}
        <main
          ref={mainRef}
          tabIndex={-1}
          className="flex flex-1 flex-col items-center justify-center px-4 py-10"
        >
          {renderState({
            state,
            token,
            invitation,
            sign_in_path,
            support_email,
            t,
          })}
        </main>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// State renderer
// ---------------------------------------------------------------------------

type RenderArgs = InvitationsAcceptProps & {
  t: ReturnType<typeof useTranslation>["t"]
}

function renderState(args: RenderArgs) {
  switch (args.state) {
    case "valid":
      return args.invitation ? (
        <ValidAcceptForm invitation={args.invitation} token={args.token} />
      ) : null
    case "expired":
      return (
        <ExpiredState
          signInPath={args.sign_in_path}
          supportEmail={args.support_email}
          invitation={args.invitation}
        />
      )
    case "accepted":
      return (
        <AcceptedState
          signInPath={args.sign_in_path}
          supportEmail={args.support_email}
          invitation={args.invitation}
        />
      )
    case "revoked":
      return (
        <RevokedState
          signInPath={args.sign_in_path}
          supportEmail={args.support_email}
          invitation={args.invitation}
        />
      )
    case "mismatch":
      return args.invitation ? (
        <MismatchState invitation={args.invitation} token={args.token} />
      ) : null
    case "rejected":
      return <RejectedState supportEmail={args.support_email} />
  }
}

// ---------------------------------------------------------------------------
// Valid — the form
// ---------------------------------------------------------------------------

interface ValidAcceptFormProps {
  invitation: InvitationPayload
  token: string
}

function ValidAcceptForm({ invitation, token }: ValidAcceptFormProps) {
  const { t } = useTranslation()
  const online = useOnline()
  // Backend may send `target_account.type === 'admin'` (no tenant) — the D1
  // role-label helper treats any non-brand/non-agency as the OKS scope, so
  // we coerce to `null` before passing it in.
  const rawAccountType = invitation.target_account.type
  const accountType: InvitationAccountType =
    rawAccountType === "brand" || rawAccountType === "agency"
      ? rawAccountType
      : null
  const accountName =
    invitation.target_account.name ?? t("invitations.accept.oks_brand_name")
  // Backend invariant — `valid` state always carries `role`. The `??`
  // branch never runs in production; it's a defence-in-depth default so a
  // malformed payload doesn't crash the page.
  const role = roleLabel(t, {
    accountType,
    role: invitation.role ?? "creator",
  })

  // Creator invite (target_account.type === 'brand') gets the branded dark
  // hero — brand identity surfaces as DATA (logo + name), not as a theme
  // override. OKS chrome stays neutral. Admin + OKS invites get the OKS hero.
  const showBrandHero = accountType === "brand"

  // Existing users see the "confirm attach" copy variant; payload + URL are
  // identical so the backend picks the branch at accept time.
  const copyBucket = invitation.existing_user ? "existing" : "new"

  return (
    <div className="flex w-full max-w-md flex-col gap-6">
      <header
        className={cn(
          "flex flex-col items-center gap-3 text-center",
          showBrandHero && "rounded-lg px-6 py-8",
        )}
      >
        <div className="bg-muted text-foreground flex size-16 items-center justify-center rounded-full">
          {/*
            OKS-303 — `<BrandLogo>` renders the `<img>` AND the
            `BrandInitialMonogram` fallback (with `onError` swap, UX Spec
            §3). `text-2xl` keeps the letter proportional inside the 64px
            tile (~4:1 ratio mirrors OKS-266's size-8 text-sm pairing).

            `target_account.name` is `string | null` in the type — null
            only on terminal/admin invites that never render this branch
            (per the state matrix in `types/invitations.ts`). Coerce to
            `""` so TS is happy; the monogram's own defensive `"?"`
            fallback covers any edge case where it slips through.
          */}
          <BrandLogo
            logoUrl={invitation.target_account.logo_url ?? null}
            name={invitation.target_account.name ?? ""}
            className="size-16 rounded-full"
            monogramClassName="text-2xl"
            testId="invitation-accept-brand-logo"
          />
        </div>
        <div className="space-y-1">
          {/*
           * Heading visually carries both the target account + the role label.
           * We use `<Trans>` with index tags (<1>{{account}}</1>, <3>{{role}}</3>)
           * in the translation strings so each branding facet gets its own
           * `data-testid` hook — locale-agnostic selectors for the QA contract
           * (QA-MESSAGE-to-frontend-oks-11.md). No sr-only side-channel, no
           * duplicate DOM, no double screen-reader announcements.
           *
           * Index pairing with the YAML:
           *   - `title`  → <1> = account, <3> = role
           *   - `inviter` → <1> = inviter
           */}
          <h1 className="text-foreground text-xl font-semibold tracking-tight">
            <Trans
              i18nKey="invitations.accept.valid.title"
              values={{ account: accountName, role }}
              components={[
                <span key="0" />,
                <span key="1" data-testid="invite-target-account" />,
                <span key="2" />,
                <span key="3" data-testid="invite-role" />,
              ]}
            />
          </h1>
          <p className="text-muted-foreground text-sm">
            <Trans
              i18nKey="invitations.accept.valid.inviter"
              values={{ inviter: invitation.inviter.name }}
              components={[
                <span key="0" />,
                <span key="1" data-testid="invite-inviter-name" />,
              ]}
            />
          </p>
        </div>
      </header>

      {!online && (
        <Alert data-testid="invite-accept-offline-alert">
          <AlertDescription>
            {t("invitations.accept.offline.banner")}
          </AlertDescription>
        </Alert>
      )}

      <Form
        method="post"
        action={acceptInvitationPath(token)}
        resetOnSuccess={["user[password]"]}
        className="flex flex-col gap-5"
        data-testid="invite-accept-form"
      >
        {({ processing, errors }) => (
          <>
            <div className="grid gap-2">
              <Label htmlFor="invite-name-input">
                {t("invitations.accept.valid.name_label")}
                {/* OKS-728 — required marker; native `required` dropped so a
                    blank submit reaches the server for a translated inline
                    error. Name is presence-validated (ctx :invitation_accept). */}
                <span className="text-destructive ml-0.5" aria-hidden="true">
                  *
                </span>
              </Label>
              {/*
               * `id` + `data-testid` kept in sync per the QA contract
               * (`invite-name-input`). Capybara's `fill_in` matches against
               * `id`/`name`/`placeholder`/label-text — aligning the `id` lets
               * the RSpec system spec locate the input by the same selector
               * the contract prescribes.
               */}
              <Input
                id="invite-name-input"
                name="user[name]"
                type="text"
                aria-required="true"
                aria-invalid={errors.name ? "true" : undefined}
                autoFocus={!invitation.existing_user}
                autoComplete="name"
                placeholder={t("invitations.accept.valid.name_placeholder")}
                aria-describedby={errors.name ? "invite-name-error" : undefined}
                data-testid="invite-name-input"
              />
              <InputError id="invite-name-error" messages={errors.name} />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="invite-email">
                {t("invitations.accept.valid.email_label")}
              </Label>
              {/*
               * Rule 5 — email is pre-filled + NOT editable. We use `readOnly`
               * (not `disabled`) so screen readers still announce it and the
               * visual context is preserved. `aria-describedby` points at the
               * "bound to this email" hint so AT reads the reasoning.
               */}
              {/*
               * NO `name` attr — email is display-only (PRD Rule 5: email is
               * invite-bound on the backend and is never accepted from
               * params). Omitting `name` means the browser doesn't include
               * this value in the submit payload, keeping the Inertia POST
               * body tight.
               */}
              <Input
                id="invite-email"
                type="email"
                value={invitation.email}
                readOnly
                aria-describedby="invite-email-hint"
                tabIndex={-1}
                data-testid="invite-accept-email"
              />
              <p
                id="invite-email-hint"
                className="text-muted-foreground text-xs"
              >
                {t("invitations.accept.valid.email_hint")}
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="invite-password-input">
                {t(`invitations.accept.valid.${copyBucket}.password_label`)}
                <span className="text-destructive ml-0.5" aria-hidden="true">
                  *
                </span>
              </Label>
              <Input
                id="invite-password-input"
                name="user[password]"
                type="password"
                aria-required="true"
                aria-invalid={errors.password ? "true" : undefined}
                autoFocus={invitation.existing_user}
                autoComplete="new-password"
                aria-describedby={
                  errors.password ? "invite-password-error" : undefined
                }
                data-testid="invite-password-input"
              />
              <InputError
                id="invite-password-error"
                messages={errors.password}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="invite-password-confirmation">
                {t("invitations.accept.valid.password_confirmation_label")}
                <span className="text-destructive ml-0.5" aria-hidden="true">
                  *
                </span>
              </Label>
              {/*
               * OKS-441 — Confirmation is a REAL, server-validated field.
               * `name="user[password_confirmation]"` serialises it into the
               * POST under the same `user[…]` namespace as name + password
               * (permitted by `accept_params`). The server is the source of
               * truth: `User`'s `:invitation_accept`-scoped presence rule
               * rejects a blank confirmation and `has_secure_password`'s
               * confirmation validator rejects a mismatch — both surface as
               * `errors.password_confirmation` below.
               * OKS-728 — native `required` dropped (was a "secondary browser
               * nudge"); required-ness is now signalled by the `*` marker +
               * `aria-required` and enforced solely server-side, so a blank
               * confirmation renders the translated inline error, not a native
               * bubble.
               */}
              <Input
                id="invite-password-confirmation"
                name="user[password_confirmation]"
                type="password"
                aria-required="true"
                aria-invalid={errors.password_confirmation ? "true" : undefined}
                autoComplete="new-password"
                aria-describedby={
                  errors.password_confirmation
                    ? "invite-password-confirmation-error"
                    : undefined
                }
                data-testid="invite-accept-password-confirmation"
              />
              <InputError
                id="invite-password-confirmation-error"
                messages={errors.password_confirmation}
              />
            </div>

            <InputError messages={errors.base} />

            <Button
              type="submit"
              className="mt-2 min-h-11 w-full"
              disabled={processing || !online}
              data-testid="invite-submit"
            >
              {processing && <Spinner />}
              {t("invitations.accept.valid.cta", { account: accountName })}
            </Button>
          </>
        )}
      </Form>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Non-valid state pages — share an EmptyState layout
// ---------------------------------------------------------------------------

interface NonValidStateProps {
  signInPath: string
  supportEmail: string
  invitation?: InvitationPayload
}

function ExpiredState({
  signInPath,
  supportEmail,
  invitation,
}: NonValidStateProps) {
  const { t } = useTranslation()
  return (
    <EmptyState
      icon={Clock}
      role="status"
      title={t("invitations.accept.expired.title")}
      description={t("invitations.accept.expired.body", {
        inviter: invitation?.inviter.name,
      })}
      actions={
        <>
          <Button asChild variant="outline">
            <Link href={signInPath}>
              {t("invitations.accept.common.sign_in_cta")}
            </Link>
          </Button>
          <Button
            asChild
            variant="outline"
            data-testid="invite-expired-support"
          >
            <a href={`mailto:${supportEmail}`}>
              {t("invitations.accept.common.contact_support")}
            </a>
          </Button>
        </>
      }
    />
  )
}

function AcceptedState({
  signInPath,
  supportEmail,
  invitation,
}: NonValidStateProps) {
  const { t } = useTranslation()
  return (
    <EmptyState
      icon={CheckCircle2}
      role="status"
      title={t("invitations.accept.accepted.title")}
      description={t("invitations.accept.accepted.body", {
        account: invitation?.target_account.name,
      })}
      actions={
        <>
          <Button asChild>
            <Link href={signInPath}>
              {t("invitations.accept.common.sign_in_cta")}
            </Link>
          </Button>
          <Button
            asChild
            variant="outline"
            data-testid="invite-accepted-support"
          >
            <a href={`mailto:${supportEmail}`}>
              {t("invitations.accept.common.contact_support")}
            </a>
          </Button>
        </>
      }
    />
  )
}

function RevokedState({
  signInPath,
  supportEmail,
  invitation,
}: NonValidStateProps) {
  const { t } = useTranslation()
  // Visual parity with expired (no revoker attribution — see UX Spec F5 +
  // PRD §5). Icon differs purely for internal clarity; copy references the
  // original inviter so the invitee knows who to chase — NOT the revoker.
  return (
    <EmptyState
      icon={XCircle}
      role="status"
      title={t("invitations.accept.revoked.title")}
      description={t("invitations.accept.revoked.body", {
        inviter: invitation?.inviter.name,
      })}
      actions={
        <>
          <Button asChild variant="outline">
            <Link href={signInPath}>
              {t("invitations.accept.common.sign_in_cta")}
            </Link>
          </Button>
          <Button
            asChild
            variant="outline"
            data-testid="invite-revoked-support"
          >
            <a href={`mailto:${supportEmail}`}>
              {t("invitations.accept.common.contact_support")}
            </a>
          </Button>
        </>
      }
    />
  )
}

// ---------------------------------------------------------------------------
// Mismatch — Rule 12 / D3: signed in as a different email
// ---------------------------------------------------------------------------

interface MismatchStateProps {
  invitation: InvitationPayload
  token: string
}

function MismatchState({ invitation, token }: MismatchStateProps) {
  const { t } = useTranslation()

  // Pull the current session id off Inertia shared props so log-out posts to
  // the real session row. If it's missing we disable the button — an
  // anonymous viewer doesn't need a log-out affordance anyway (the mismatch
  // state only renders when a user IS signed in per Rule 12).
  // Typed via `SharedProps` so the cast is localised.
  const session = useSharedCurrentSession()

  function handleSignOut() {
    if (!session?.id) return
    // Delete the session, then immediately revisit the accept link so the
    // invitee lands back on the same token but now in an anonymous context.
    // Inertia's `onSuccess` fires after the DELETE 303 redirect lands.
    router.delete(sessionPath({ id: session.id }), {
      onSuccess: () => {
        router.visit(acceptInvitationPath(token))
      },
    })
  }

  const maskedEmail = maskEmail(invitation.email)

  return (
    <EmptyState
      icon={ShieldAlert}
      role="status"
      title={t("invitations.accept.mismatch.title")}
      description={t("invitations.accept.mismatch.body", {
        email: maskedEmail,
      })}
      actions={
        <>
          <Button
            onClick={handleSignOut}
            variant="destructive"
            disabled={!session?.id}
            data-testid="invite-mismatch-logout"
          >
            <LogOut aria-hidden="true" />
            {t("invitations.accept.mismatch.logout")}
          </Button>
          <Button asChild variant="outline">
            <Link href={rootPath()}>
              {t("invitations.accept.mismatch.cancel")}
            </Link>
          </Button>
        </>
      }
    />
  )
}

// ---------------------------------------------------------------------------
// Rejected — Rule 8a / D4: existing brand/agency user, second-account invite
// ---------------------------------------------------------------------------

function RejectedState({ supportEmail }: { supportEmail: string }) {
  const { t } = useTranslation()
  return (
    <EmptyState
      icon={MailX}
      role="status"
      title={t("invitations.accept.rejected.title")}
      description={t("invitations.accept.rejected.body")}
      actions={
        <Button
          asChild
          variant="destructive"
          data-testid="invite-rejected-support"
        >
          <a href={`mailto:${supportEmail}`}>
            {t("invitations.accept.rejected.support_cta")}
          </a>
        </Button>
      }
    />
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Mask the invitee email for display in the mismatch dialog. Keeps the first
 * character + the domain so the user can visually match against the invite
 * they received, without exposing the full local-part to an over-the-shoulder
 * observer who might not be the intended invitee.
 *
 * "ada.lovelace@example.com" → "a***@example.com"
 * "x@example.com"            → "*@example.com" (single-char local-part edge case)
 */
function maskEmail(email: string): string {
  const [local, domain] = email.split("@")
  if (!local || !domain) return email
  if (local.length <= 1) return `*@${domain}`
  return `${local[0]}***@${domain}`
}

/**
 * Small typed reader over Inertia's shared props. Returns `null` when no
 * session is present (anonymous visitor on the accept page).
 */
function useSharedCurrentSession(): CurrentSession | null {
  const { props } = usePage<Partial<SharedProps>>()
  return props.currentSession ?? null
}
